import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { connect, ConnectedProps, useDispatch } from 'react-redux';
import { v4 as uuid } from 'uuid';
import { useQueryUniqueObra } from 'Hooks/obras/useQueryUniqueObra';
import { useQueryFiles } from 'Hooks/files/useQueryFiles';
import { IGlobalReducerState } from 'Store/Base/interface/IGlobalReducerState';
import { UploadActions } from 'Store/Upload/Upload.actions';
import { FederatedViewerActions } from 'Store/FederatedViewer/FederatedViewer.actions';
import { NomenclaturaActions } from 'Store/Nomenclatura/Nomenclatura.actions';
import { IUpdateStatusInViewer } from 'Data/interfaces/ListagemArquivos/IUpdateStatusInViewer';
import { IDisciplineViewerList } from 'Data/interfaces/FederatedViewer/IDisciplineViewerList';
import { IFileData } from 'Data/interfaces/ListagemArquivos/IListFilesResponse';
import { IUploadFileData } from 'Data/interfaces/Upload/IUploadFileData';
import { DisciplineIconsEnum } from 'Pages/Disciplinas';
import Button from 'Components/UI/Button';
import Modal from 'Components/UI/Modal';
import LoadingUploadDisciplineList from 'Components/UI/CustomLoading/LoadingUploadDisciplineList';
import { IconName } from 'Components/UI';
import FileDestinationHeader from './components/FileDestinationHeader';
import FileDestinationListDisciplines from './components/FileDestinationListDisciplines';
import FileDestinationListChildren from './components/FileDestinationListChildren';
import styles from './FileDestination.module.scss';

export interface IFileDestination {
  visible: boolean;
  file: File;
  currentDiscipline?: any;
  externalLoading?: boolean;
  constructionSiteId: number;
  fromMaleta?: boolean;
  disciplineList: IDisciplineViewerList[];
  onCancel: () => void;
}

export interface IFileFinalPath {
  folderId: string;
  disciplineApiFolderId: string;
  ConstructionSiteDisciplinesId: number;
  trackFolderName?: string;
}

export interface IBreadcrumbPath {
  name: string;
  folderId: string;
  ConstructionSiteDisciplinesId?: number;
}

const FileDestination: FC<Props> = ({
  visible,
  file,
  constructionSiteId,
  fromMaleta,
  loading,
  disciplineList,
  nomenclaturaData,
  onCancel,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { data: obra, isFetching: disciplinesLoading } = useQueryUniqueObra({
    csId: constructionSiteId,
    navigate,
  });

  const [fileFinalPath, setFileFinalPath] = useState({} as IFileFinalPath);
  const [disciplineFolder, setDisciplineFolder] = useState<IDisciplineViewerList | null>(null);
  const [breadCrumbs, setBreadCrumbs] = useState<IBreadcrumbPath[]>([]);
  const [searchDisciplineFile, setSearchDisciplineFile] = useState('');
  const [searchFile, setSearchFile] = useState('');
  const [selectedFileFromMaleta, setSelectedFileFromMaleta] = useState<IFileData | null>(null);
  const [sentFile, setSentFile] = useState(false);
  const [folderId, setFolderId] = useState('');
  const {
    data: listFilesData,
    isFetching: listFilesDataLoading,
  } = useQueryFiles({
    constructionSiteId,
    folderId,
    loadObsoleteFiles: false,
    ignoreBreadCrumb: true,
  });

  const filteredDisciplines = useMemo(() => {
    if (fromMaleta && obra?.Disciplines) {
      return obra.Disciplines?.filter((discipline: any) => {
        if (discipline.CustomName) {
          return discipline?.CustomName.toLowerCase().includes(searchDisciplineFile.toLowerCase());
        } else {
          return discipline?.Discipline.Name.toLowerCase().includes(searchDisciplineFile.toLowerCase());
        }
      });
    }
    return obra?.Disciplines;
  }, [obra?.Disciplines, fromMaleta, searchDisciplineFile]);

  const filteredFiles = useMemo(() => {
    if (fromMaleta && listFilesData.ListFiles && listFilesData.ListFiles.length > 0) {
      return listFilesData.ListFiles.filter((fileList) => {
        return fileList.Name.toLowerCase().includes(searchFile.toLowerCase());
      });
    }
    return listFilesData.ListFiles;
  }, [listFilesData.ListFiles, fromMaleta, searchFile]);

  const excludeListFiles = useMemo(() => {
    if (fromMaleta && disciplineList) {
      const disciplineExist = disciplineList.find((discipline) => {
        return discipline.ConstructionSiteDisciplinesId ===
          disciplineFolder?.ConstructionSiteDisciplinesId;
      });
      if (disciplineExist?.FilesList && disciplineExist?.FilesList?.length > 0) {

        return disciplineExist.FilesList.map((disciplineFile) => {
          return disciplineFile.Identifier.ApiId || disciplineFile.Identifier.ItemId;
        });
      }
    }
    return [];
  }, [disciplineList, fromMaleta, disciplineFolder]);

  useEffect(() => {
    handleListDisciplines();

    if (!nomenclaturaData.VersionFields) {
      dispatch(NomenclaturaActions.getNomenclature());
    }
  }, []);

  useEffect(() => {
    if (sentFile && loading) {
      closeModal();
    }
  }, [sentFile, loading]);

  const handleDisciplineFolder = (currentDisciplineFolder: IDisciplineViewerList | null) => {
    setDisciplineFolder(currentDisciplineFolder);
    clearAllSearch();
  };

  const handleIconLeft = (name: string): IconName => {
    if ((DisciplineIconsEnum as any)[name]) {
      return (DisciplineIconsEnum as any)[name];
    }
    return 'menuContexto';
  };

  const handleOpenFolder = (currentFolderId: string, ConstructionSiteDisciplinesId?: number, folderName?: string) => {
    setSelectedFileFromMaleta(null);
    clearAllSearch();
    setFolderId(currentFolderId);

    const filePath: IFileFinalPath = {
      folderId: currentFolderId,
      disciplineApiFolderId: disciplineFolder?.DisciplineApiFolderId || currentFolderId,
      ConstructionSiteDisciplinesId:
        disciplineFolder?.ConstructionSiteDisciplinesId ||
        ConstructionSiteDisciplinesId ||
        0,
      trackFolderName: folderName,
    };
    setFileFinalPath(filePath);

    setBreadCrumbs((prevState) => {
      const folderIndex = prevState.findIndex(
        (state) => state.folderId === currentFolderId,
      );
      if (folderIndex === -1 && folderName) {
        return [...prevState, {
          folderId: currentFolderId,
          name: folderName,
          ConstructionSiteDisciplinesId: disciplineFolder?.ConstructionSiteDisciplinesId ||
            ConstructionSiteDisciplinesId,
        }];
      }
      if (folderIndex === -1 && !folderName) {
        return [];
      }
      if (folderIndex >= 0 && folderName) {
        if (folderIndex === 0) {
          return [{
            folderId: currentFolderId,
            name: folderName,
            ConstructionSiteDisciplinesId: disciplineFolder?.ConstructionSiteDisciplinesId ||
              ConstructionSiteDisciplinesId,
          }];
        } else {
          return prevState.slice(0, folderIndex + 1);
        }
      }
      return prevState;
    });
  };

  const handleListDisciplines = () => {
    setBreadCrumbs([]);
    setSelectedFileFromMaleta(null);
    setDisciplineFolder(null);
    clearAllSearch();
  };

  const closeModal = () => {
    setSelectedFileFromMaleta(null);
    setFileFinalPath({} as any);
    setDisciplineFolder(null);
    clearAllSearch();
    onCancel();
  };

  const isLoading = useMemo(() => {
    if (listFilesDataLoading || disciplinesLoading) {
      return true;
    } else {
      return false;
    }
  }, [listFilesDataLoading, disciplinesLoading]);

  const confirmUploadFilePath = () => {
    if (obra && disciplineFolder) {
      setSentFile(true);
      if (fromMaleta && selectedFileFromMaleta) {
        const updateStatusInViewer: IUpdateStatusInViewer = {
          fileApiId: selectedFileFromMaleta.Identifier.ApiId || selectedFileFromMaleta.Identifier.ItemId,
          constructionSiteId,
          constructionSiteDisciplineId: disciplineFolder.ConstructionSiteDisciplinesId,
          api: obra.Api,
          inViewer: true,
          discipline: disciplineFolder,
          currentFile: selectedFileFromMaleta,
        };
        dispatch(FederatedViewerActions.updateStatusInViewer(updateStatusInViewer));

      } else {
        const fileIdUpload = uuid();
        const uploadFiles: IUploadFileData[] = [{
          csId: constructionSiteId,
          folderId: fileFinalPath.folderId,
          trackFolderName: fileFinalPath.trackFolderName,
          disciplineApiFolderId: fileFinalPath.disciplineApiFolderId,
          trackDisciplineName: disciplineFolder.Discipline.Name || disciplineFolder.CustomName,
          file,
          isSending: false,
          sentSize: 0,
          totalSize: file.size,
          uploadCompleted: false,
          uploadError: false,
          Api: obra?.Api,
          inViewer: true,
          constructionSiteDisciplineId: fileFinalPath.ConstructionSiteDisciplinesId,
          discipline: disciplineFolder,
          hasRevisionControl: obra.VersionConfig.HasRevisionControl,
          fileIdUpload,
        }];
        dispatch(UploadActions.addNewUploadFiles(uploadFiles));
        closeModal();
      }
    }
  };

  const handleConfirmFileFromMaleta = (item: IFileData) => {
    setSelectedFileFromMaleta(item);
  };

  const handleSearch = (value: string) => {
    if (!disciplineFolder) {
      setSearchFile('');
      setSearchDisciplineFile(value);
    } else {
      setSearchFile(value);
      setSearchDisciplineFile('');
    }
  };

  const clearAllSearch = () => {
    setSearchDisciplineFile('');
    setSearchFile('');
  };

  const buttons = (
    <div className={styles['buttons']}>
      <Button
        onClick={closeModal}
        type='text'
        className={styles['buttonCancel']}
      >
        Cancelar
      </Button>
      <Button
        onClick={() => confirmUploadFilePath()}
        type='primary'
        className={`${styles['buttonConfirm']} ${styles['buttonConfirmMaleta']}`}
        disabled={fromMaleta
          ? selectedFileFromMaleta === null
          : !disciplineFolder
        }
        loading={loading}
      >
        {fromMaleta ? 'Selecionar arquivo' : 'Enviar aqui'}
      </Button>
    </div>
  );

  return (
    <Modal
      title={fromMaleta ? 'Selecione um arquivo' : 'Selecione um destino'}
      width={500}
      visible={visible}
      onCancel={closeModal}
      footerButtons={buttons}
    >
      <div className={styles['wrapper']}>
        <FileDestinationHeader
          value={searchDisciplineFile || searchFile}
          fromMaleta={fromMaleta}
          onSearch={handleSearch}
          disciplineFolder={disciplineFolder}
          onListDisciplines={handleListDisciplines}
          isLoading={isLoading}
          onOpenFolder={handleOpenFolder}
          onIconLeft={handleIconLeft}
          breadCrumbs={breadCrumbs}
        />
        <div className={styles['main']}>
          {(isLoading || (!filteredDisciplines && filteredFiles.length <= 0)) && (
            <LoadingUploadDisciplineList multiple={12} />
          )}
          {!isLoading &&
            ((filteredDisciplines && filteredDisciplines.length > 0 &&
              (!searchFile && !disciplineFolder && filteredFiles.length <= 0)) ? (
            <FileDestinationListDisciplines
              filteredDisciplines={filteredDisciplines}
              onDisciplineFolder={handleDisciplineFolder}
              onOpenFolder={handleOpenFolder}
              onIconLeft={handleIconLeft}
              searchDisciplineFile={searchDisciplineFile}
            />
          ) : (
            <FileDestinationListChildren
              filteredFiles={filteredFiles}
              fromMaleta={fromMaleta}
              selectedFileFromMaleta={selectedFileFromMaleta}
              onOpenFolder={handleOpenFolder}
              disciplineFolder={disciplineFolder}
              onConfirmFileFromMaleta={handleConfirmFileFromMaleta}
              searchFile={searchFile}
              excludeListFiles={excludeListFiles}
            />
          ))}
        </div>
      </div>
    </Modal>
  );
};

const mapState = (state: IGlobalReducerState) => ({
  loading: state.federatedViewer.isLoading,
  nomenclaturaData: state.nomenclatura.NomenclatureData,
});

const connector = connect(mapState);

type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux & IFileDestination;

export default connector(FileDestination);
